import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import type React from "react";
import { useSelector } from "react-redux";
import { CommonButton } from "~/components/ui/commonButton";
import type { DocumentType } from "~/config/documents";
import CheckWhite from "~/images/check_white.svg?react";
import EditWhite from "~/images/edit_white.svg?react";
import ExportPrimary from "~/images/export_primary.svg?react";
import type { RootState } from "~/store";
import { toDisplayString } from "~/utils/customDate";
import styles from "./index.module.scss";

interface ModifiedBy {
  display_name: string;
}

interface CommonDocumentHeaderProps {
  title: string;
  documentType: DocumentType;
  createdAt: string;
  modifiedAt?: string;
  modifiedBy?: ModifiedBy;
  isEditable: boolean;
  isLoading: boolean;
  isHistory?: boolean;
  handleCancel?: () => void;
  toggleEditMode?: () => void;
  handleExportClick?: () => void;
  handleHistoryClick: () => void;
  handleBackClick?: () => void;
  showEditButton?: boolean;
  showExportButton?: boolean;
}

const CommonDocumentHeader: React.FC<CommonDocumentHeaderProps> = ({
  title,
  documentType: _documentType,
  createdAt,
  modifiedAt,
  modifiedBy,
  isEditable,
  isLoading,
  isHistory = true,
  handleCancel,
  toggleEditMode,
  handleExportClick,
  handleHistoryClick,
  handleBackClick,
  showEditButton = false,
  showExportButton = false,
}) => {
  // Get current user and project members from Redux store
  const { currentUser, projectMembers } = useSelector((state: RootState) => state.auth);

  // Function to check if current user can export documents
  const canExportDocuments = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );
    const canExport =
      currentUserInProject?.role === "owner" || currentUserInProject?.role === "contributor";
    return canExport;
  };

  const formattedCreatedAt = toDisplayString(createdAt);
  const formattedModifiedAt = modifiedAt ? toDisplayString(modifiedAt) : null;
  const isLoadingDate = (s?: string) => !s || s.includes("NaN");
  const isDateSkeleton =
    isLoading || (isLoadingDate(formattedModifiedAt!) && isLoadingDate(formattedCreatedAt));
  const displayName = modifiedBy?.display_name ?? "REQXIGN";

  const renderDateInfo = () => {
    if (isDateSkeleton) {
      return (
        <div className={styles["commonDocumentHeader-date"]}>
          <span className={styles["skeletonLine"]} data-testid="date-skeleton"></span>
        </div>
      );
    }
    if (formattedModifiedAt) {
      return (
        <div className={styles["commonDocumentHeader-date"]}>
          <span>最終更新:</span>
          <span>{formattedModifiedAt}</span>
        </div>
      );
    }
    return (
      <div className={styles["commonDocumentHeader-date"]}>
        <span>{isHistory ? "生成日:" : "作成日:"}</span>
        <span>{formattedCreatedAt}</span>
      </div>
    );
  };

  return (
    <div className={styles["commonDocumentHeader"]}>
      <div className={styles["commonDocumentHeader-inner"]}>
        <div className={styles["commonDocumentHeader-left"]}>
          <h1 className="flex items-center gap-2" data-testid="doc-title-display">
            {!isHistory && (
              <button
                type="button"
                className={styles["icon-back-arrow"]}
                onClick={handleBackClick}
                data-testid="back-button"
                aria-label="戻る"
              />
            )}
            {title}
          </h1>
          <div className={styles["commonDocumentHeader-dates"]}>
            {renderDateInfo()}
            {isHistory && (
              <div className={styles["commonDocumentHeader-user"]}>
                <AccountCircleIcon
                  {...(styles["documentHeader__info__svg"] && {
                    className: styles["documentHeader__info__svg"],
                  })}
                />
                <p>{displayName}</p>
                {isHistory && (
                  <button
                    type="button"
                    onClick={handleHistoryClick}
                    className={styles["commonDocumentHeader-history"]}
                    aria-label="生成履歴を表示"
                  >
                    生成履歴
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {(showEditButton || showExportButton) && (
          <div className={styles["commonDocumentHeader-right"]}>
            <div className={styles["commonDocumentHeader-buttons"]}>
              {showEditButton && (
                <>
                  {isEditable && (
                    <CommonButton variant="label" {...(handleCancel && { onClick: handleCancel })}>
                      キャンセル
                    </CommonButton>
                  )}
                  <CommonButton
                    icon={isEditable ? <CheckWhite /> : <EditWhite />}
                    {...(toggleEditMode && { onClick: toggleEditMode })}
                    disabled={!canExportDocuments()}
                    data-testid={isEditable ? "doc-save" : "doc-edit-button"}
                  >
                    {isEditable ? " 保存 " : " 編集 "}
                  </CommonButton>
                </>
              )}
              {showExportButton && (
                <CommonButton
                  icon={<ExportPrimary />}
                  variant="icon"
                  {...(handleExportClick && { onClick: handleExportClick })}
                  disabled={isEditable || !canExportDocuments()}
                  data-testid="doc-export-button"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonDocumentHeader;
